export enum SearchMode {
  FIND_ANY,
  FIND_FIRST,
  FIND_LAST
}

export class SearchUtils {

  /**
   * Given a **sorted array** in **ascending order** and a comparator, return an index where and new item (which the comparator would
   * consider to be equal, i.e. for which the comparator would return 0) can be inserted to keep the array in sorted order. The
   * implementation uses binary search.
   * @param a array that **must** be in sorted ascending order
   * @param c comparator
   * @param sm controls what index to return in case new item (i.e. an item for which the comparator would return 0) is equal to multiple
   * entries in the array. Possible values:
   * - {@link SearchMode.FIND_ANY}  return the first found index that meets the criteria
   * - {@link SearchMode.FIND_FIRST}  return the lowest possible index that would meet the criteria
   * - {@link SearchMode.FIND_LAST}  return the highest possible index that would meet the criteria
   * @return index where an new item can be inserted in the array to preserve the sort order
   */
  static sortedIndex<T>(a: Array<T>, c: (a: T) => number, sm: SearchMode = SearchMode.FIND_ANY): number {
    if (a && a.length) {
      let start = 0;
      let end = a.length - 1;
      // Iterate while start not meets end
      while (start <= end) {
        // Find the mid index
        let mid = Math.floor((start + end) / 2);
        // Use the comparator
        const cr = c(a[mid]);
        if (cr === 0) {
          switch (sm) {
            case (SearchMode.FIND_FIRST): {
              // Look back through the array in case there are equal entries
              while (mid > 0 && c(a[mid - 1]) === 0) {
                mid--;
              }
              break;
            }
            case (SearchMode.FIND_LAST): {
              // Want to return the latest index possible, so if the entries are equal, return the index after
              mid++;
              // Look forward through the array in case there are more equal entries
              while (mid < a.length && c(a[mid]) === 0) {
                mid++;
              }
              break;
            }
          }
          return mid;
        } else if (cr > 0) {
          start = mid + 1;
        } else {
          end = mid - 1;
        }
      }
      return start;
    } else {
      return -1;
    }
  }
}
